<script setup>
// Template form dialog for campaign templates
import { ref, watch, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    default: null,
  },
  categories: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'save']);

const dialogRef = ref(null);

const formState = ref({
  name: '',
  content: '',
  category: '',
  description: '',
  variables: '',
});

const isSubmitting = ref(false);

const rules = {
  name: { required, minLength: minLength(3) },
  content: { required, minLength: minLength(10) },
};

const v$ = useVuelidate(rules, formState);

const isEditing = computed(() => !!props.template?.id);

const dialogTitle = computed(() =>
  isEditing.value ? 'Editar Template' : 'Criar Template'
);

const categoryOptions = computed(() => {
  const existingCategories = props.categories.map(c => ({
    value: c,
    label: c,
  }));
  return [
    { value: '', label: 'Sem categoria' },
    ...existingCategories,
    { value: '__new__', label: '+ Nova categoria' },
  ];
});

const showNewCategoryInput = ref(false);
const newCategoryName = ref('');

watch(
  () => formState.value.category,
  newVal => {
    if (newVal === '__new__') {
      showNewCategoryInput.value = true;
      formState.value.category = '';
    }
  }
);

watch(
  () => props.template,
  newTemplate => {
    if (newTemplate) {
      formState.value = {
        name: newTemplate.name || '',
        content: newTemplate.content || '',
        category: newTemplate.category || '',
        description: newTemplate.description || '',
        variables: (newTemplate.variables || []).join(', '),
      };
    }
  },
  { immediate: true }
);

const resetForm = () => {
  formState.value = {
    name: '',
    content: '',
    category: '',
    description: '',
    variables: '',
  };
  showNewCategoryInput.value = false;
  newCategoryName.value = '';
  v$.value.$reset();
};

const open = () => {
  if (!props.template) {
    resetForm();
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;

  try {
    const category = showNewCategoryInput.value
      ? newCategoryName.value
      : formState.value.category;

    const variables = formState.value.variables
      .split(',')
      .map(v => v.trim())
      .filter(v => v);

    const payload = {
      name: formState.value.name,
      content: formState.value.content,
      category: category || null,
      description: formState.value.description || null,
      variables: variables.length > 0 ? variables : null,
    };

    if (isEditing.value) {
      payload.id = props.template.id;
    }

    emit('save', payload);
    close();
  } finally {
    isSubmitting.value = false;
  }
};

// Detect variables in content
const detectedVariables = computed(() => {
  const content = formState.value.content || '';
  const matches = content.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
});

// Helper to format variable display (avoid Vue template parser issues)
const formatVariable = variable => `{{${variable}}}`;
const variablePlaceholder = '{{variavel}}';

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="formState.name"
        label="Nome do template"
        placeholder="Ex: Boas-vindas, Follow-up, Promoção..."
        :message="
          v$.name.$error ? 'Nome é obrigatório (mín. 3 caracteres)' : ''
        "
        :message-type="v$.name.$error ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12"> Categoria </label>
        <ComboBox
          v-if="!showNewCategoryInput"
          v-model="formState.category"
          :options="categoryOptions"
          placeholder="Selecione ou crie uma categoria"
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <div v-else class="flex gap-2">
          <Input
            v-model="newCategoryName"
            class="flex-1"
            placeholder="Nome da nova categoria"
          />
          <Button
            type="button"
            variant="faded"
            color="slate"
            size="sm"
            @click="showNewCategoryInput = false"
          >
            Cancelar
          </Button>
        </div>
      </div>

      <Input
        v-model="formState.description"
        label="Descrição (opcional)"
        placeholder="Breve descrição do uso deste template"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Conteúdo da mensagem
        </label>
        <TextArea
          v-model="formState.content"
          placeholder="Digite sua mensagem... Use {{nome}}, {{telefone}} para variáveis"
          rows="6"
        />
        <p v-if="v$.content.$error" class="text-xs text-n-ruby-text">
          Conteúdo é obrigatório (mín. 10 caracteres)
        </p>
        <p v-else class="text-xs text-n-slate-11">
          Use
          <code class="px-1 bg-n-alpha-2 rounded">{{
            variablePlaceholder
          }}</code>
          para inserir variáveis dinâmicas
        </p>
      </div>

      <!-- Detected variables -->
      <div
        v-if="detectedVariables.length > 0"
        class="p-3 rounded-lg bg-n-alpha-2"
      >
        <p class="text-xs font-medium text-n-slate-12 mb-2">
          Variáveis detectadas:
        </p>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="variable in detectedVariables"
            :key="variable"
            class="px-2 py-0.5 text-xs bg-n-blue/10 text-n-blue rounded-full"
          >
            {{ formatVariable(variable) }}
          </span>
        </div>
      </div>

      <Input
        v-model="formState.variables"
        label="Variáveis permitidas (opcional)"
        placeholder="nome, telefone, email, empresa..."
      />
      <p class="text-xs text-n-slate-11 -mt-2">
        Separe por vírgula. Deixe vazio para detectar automaticamente.
      </p>

      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button type="button" variant="faded" color="slate" @click="close">
          Cancelar
        </Button>
        <Button type="submit" :is-loading="isSubmitting">
          {{ isEditing ? 'Salvar alterações' : 'Criar template' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
